import React, { useState, useEffect } from 'react';
import './Tokenomics.scss';
import Header from '../Header';
import Footer from '../Footer';
import TelegramPopup from '../../Common/TelegramPopup/TelegramPopup';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';

const Tokenomics = () => {
  const [lockData, setLockData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch lock data from API
  useEffect(() => {
    const fetchLockData = async () => {
      try {
        setLoading(true);
        const response = await fetch('https://api.justlock.io/resource/resource_rdx1t42hpqvsk4t42l6aw09hwphd2axvetp6gvas9ztue0p30f4hzdwxrp');
        
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        
        const data = await response.json();
        setLockData(data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching lock data:', error);
        setError('Failed to load tokenomics data. Please try again later.');
        setLoading(false);
      }
    };

    fetchLockData();
  }, []);

  // Token allocation data
  const totalSupply = 1000000000; // 1 billion tokens
  
  // Calculate percentages based on API data or use placeholder data if API data isn't loaded yet
  const lockedTokens = lockData ? parseFloat(lockData.lock_summary.amount) : 738528506.57;
  
  // Forever locked tokens is a fixed value
  const foreverLockedTokens = 402296700;
  const foreverLockedPercent = (foreverLockedTokens / totalSupply) * 100;
  
  // Time locked tokens is the total locked tokens minus forever locked tokens
  const timeLockedTokens = lockedTokens - foreverLockedTokens;
  const timeLockedPercent = (timeLockedTokens / totalSupply) * 100;
  
  // Community tokens
  const communityTokens = totalSupply - lockedTokens;
  const communityPercent = 100 - (foreverLockedPercent + timeLockedPercent);

  // Data for pie chart
  const tokenomicsData = [
    { name: 'Forever Locked', value: foreverLockedPercent, amount: foreverLockedTokens.toFixed(0), color: '#FF3B5F' },
    { name: 'Time-Locked', value: timeLockedPercent, amount: timeLockedTokens.toFixed(0), color: '#00A3FF' },
    { name: 'Community', value: communityPercent, amount: communityTokens.toFixed(0), color: '#00FF9D' },
  ];

  // Extract lock IDs from API response to display in the details section
  const lockIds = lockData?.lock_summary?.lock_ids || [];

  // Custom tooltip for pie chart
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="custom-tooltip">
          <p className="label">{`${data.name}`}</p>
          <p className="value">{`${data.value.toFixed(2)}% (${parseInt(data.amount).toLocaleString()} tokens)`}</p>
        </div>
      );
    }
    return null;
  };
  
  // Custom legend component
  const CustomLegend = () => (
    <div className={`custom-chart-legend ${isMobile ? 'mobile' : 'desktop'}`}>
      {tokenomicsData.map((entry, index) => (
        <div className="legend-item" key={`legend-${index}`}>
          <div className="legend-color" style={{ backgroundColor: entry.color }}></div>
          <span className="legend-text">{entry.name} ({entry.value.toFixed(1)}%)</span>
        </div>
      ))}
    </div>
  );

  return (
    <div className="tokenomics-page">
      <Header />

      <main className="tokenomics-content">
        <section className="tokenomics-hero">
          <div className="container">
            <h1>REDDICKS Tokenomics</h1>
            <div className="subtitle">Total Supply: {totalSupply.toLocaleString()} Tokens</div>
          </div>
        </section>

        <section className="distribution-section">
          <div className="container">
            <h2 className="section-title">Token Distribution</h2>
            <div className="distribution-content">
              <div className="chart-container">
                {loading ? (
                  <div className="loading-spinner">
                    <div className="spinner"></div>
                    <p>Loading tokenomics data...</p>
                  </div>
                ) : error ? (
                  <div className="error-message">{error}</div>
                ) : (
                  <>
                    <ResponsiveContainer width="100%" height={isMobile ? 320 : 400}>
                      <PieChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                        <Pie
                          data={tokenomicsData}
                          cx="50%"
                          cy="45%"
                          innerRadius={({viewBox}) => Math.min(viewBox.width, viewBox.height) / 8}
                          outerRadius={({viewBox}) => Math.min(viewBox.width, viewBox.height) / 3.2}
                          paddingAngle={0}
                          dataKey="value"
                          labelLine={false}
                          label={({ cx, cy, midAngle, innerRadius, outerRadius, value, index }) => {
                            const RADIAN = Math.PI / 180;
                            const radius = 5 + innerRadius + (outerRadius - innerRadius);
                            const x = cx + radius * Math.cos(-midAngle * RADIAN);
                            const y = cy + radius * Math.sin(-midAngle * RADIAN);
                            
                            return (
                              <text 
                                x={x} 
                                y={y} 
                                fill={tokenomicsData[index].color}
                                textAnchor={x > cx ? 'start' : 'end'}
                                dominantBaseline="central"
                                className="pie-chart-label"
                              >
                                {`${value.toFixed(1)}%`}
                              </text>
                            );
                          }}
                        >
                          {tokenomicsData.map((entry, index) => (
                            <Cell 
                              key={`cell-${index}`} 
                              fill={entry.color} 
                              stroke="rgba(0,0,0,0.3)"
                              strokeWidth={1}
                            />
                          ))}
                        </Pie>
                        <Tooltip content={<CustomTooltip />} />
                      </PieChart>
                    </ResponsiveContainer>
                    <CustomLegend />
                  </>
                )}
              </div>
              
              <div className="distribution-info">
                <div className="info-card forever-locked">
                  <h3>Forever Locked</h3>
                  <div className="amount">{foreverLockedTokens.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
                  <div className="percentage">{foreverLockedPercent.toFixed(2)}%</div>
                  <p>These tokens are permanently locked and can never be accessed, effectively reducing the circulating supply and increasing token scarcity.</p>
                </div>
                
                <div className="info-card time-locked">
                  <h3>Time-Locked</h3>
                  <div className="amount">{timeLockedTokens.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
                  <div className="percentage">{timeLockedPercent.toFixed(2)}%</div>
                  <p>These tokens are locked for specific time periods, helping to ensure price stability and long-term project development.</p>
                </div>
                
                <div className="info-card liquidity">
                  <h3>Community</h3>
                  <div className="amount">{communityTokens.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
                  <div className="percentage">{communityPercent.toFixed(2)}%</div>
                  <p>REDDICKS allocates tokens to community members through play-to-earn mechanics, giveaways, airdrops, and various community initiatives, ensuring broad distribution and ecosystem growth.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="locked-details-section">
          <div className="container">
            <h2 className="section-title">Lock Details</h2>
            <div className="details-content">
              <div className="details-info">
                <p>REDDICKS takes token security seriously. A significant portion of our token supply is locked through the trusted JustLock protocol, providing transparency and confidence to our community.</p>
                <p>With <strong>{foreverLockedTokens.toLocaleString()} tokens forever locked</strong> and over <strong>{lockData ? ((parseFloat(lockData.lock_summary.amount) - foreverLockedTokens) / 1000000).toFixed(2) : ((738528506.57 - foreverLockedTokens) / 1000000).toFixed(2)} million tokens time-locked</strong>, REDDICKS ensures long-term stability and growth.</p>
                <p>The lock is verifiable on-chain, providing complete transparency to our community.</p>
                <a 
                  href="https://justlock.io/resource/resource_rdx1t42hpqvsk4t42l6aw09hwphd2axvetp6gvas9ztue0p30f4hzdwxrp" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="verify-button"
                >
                  Verify on JustLock
                </a>
              </div>
              
              <div className="lock-ids-container">
                <h3>Lock Identifiers</h3>
                <div className="lock-ids-grid">
                  {lockIds.map(id => (
                    <div key={id} className="lock-id">
                      #{id}
                    </div>
                  ))}
                </div>
                <div className="lock-ids-note">
                  Each lock ID represents a separate token locking transaction on the Radix blockchain.
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="tokenomics-benefits-section">
          <div className="container">
            <h2 className="section-title">Benefits of REDDICKS Tokenomics</h2>
            <div className="benefits-grid">
              <div className="benefit">
                <div className="benefit-icon stability"></div>
                <h3>Price Stability</h3>
                <p>With a significant portion of tokens locked, REDDICKS has a reduced free float, helping to minimize volatility and support long-term price stability.</p>
              </div>
              
              <div className="benefit">
                <div className="benefit-icon scarcity"></div>
                <h3>Token Scarcity</h3>
                <p>{foreverLockedTokens.toLocaleString()} tokens are permanently removed from circulation, increasing token scarcity and potential value over time.</p>
              </div>
              
              <div className="benefit">
                <div className="benefit-icon community"></div>
                <h3>Community Focus</h3>
                <p>REDDICKS allocates tokens to community members through play-to-earn mechanics, giveaways, airdrops, and various community initiatives, ensuring broad distribution and ecosystem growth.</p>
              </div>
              
              <div className="benefit">
                <div className="benefit-icon transparency"></div>
                <h3>Transparency</h3>
                <p>All token locks are verifiable on-chain through JustLock, ensuring full transparency and building trust with our community.</p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <TelegramPopup />
    </div>
  );
};

export default Tokenomics; 